import { Box, Group, Stack, Text } from '@mantine/core';

import { useTranslation } from 'react-i18next';

import type { SimplifiedTenderPreviewData } from '../../../shared/entities/Stream';
import { PreviewTenderCard } from './PreviewTenderCard';
import { Section } from './Section';
import type { useSearchTendersPreviewQuery } from './forms/hooks/streams.hook';
import {
  MAX_TENDER_PREVIEW_COUNT,
  usePreviewDiff,
} from './forms/hooks/usePreviewDiff.hook';
import type { SearchTendersPreviewQueryKey } from './forms/hooks/useStreamForm';

export function PreviewSection({
  queryKeys,
  previewQuery,
}: {
  queryKeys: SearchTendersPreviewQueryKey[];
  previewQuery: ReturnType<typeof useSearchTendersPreviewQuery>;
}) {
  const { preview, hasNewTenders } = usePreviewDiff(queryKeys);

  return (
    <PreviewLayout>
      <Header
        isLoading={previewQuery.isLoading}
        preview={preview}
        hasNewTenders={hasNewTenders}
      />
      {preview?.map(tender => (
        <PreviewTenderCard key={tender.id} {...tender} />
      ))}
    </PreviewLayout>
  );
}

function PreviewLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box
      p="02"
      sx={theme => ({
        backgroundColor: theme.colors.primary[1],
        borderRadius: theme.radius.md,
      })}
    >
      <Section>
        <Stack spacing="03">{children}</Stack>
      </Section>
    </Box>
  );
}

function Header({
  isLoading,
  hasNewTenders,
  preview,
}: {
  isLoading: boolean;
  hasNewTenders: boolean;
  preview: SimplifiedTenderPreviewData[] | null;
}) {
  if (isLoading || !preview) {
    return <HeaderLoadingResults />;
  }

  if (preview.length === 0) {
    return <HeaderNoNewResults />;
  }

  if (hasNewTenders) {
    return <HeaderNewResults resultsCount={preview.length} />;
  }

  return <HeaderResultsCount resultsCount={preview.length} />;
}

function HeaderLoadingResults() {
  const { t } = useTranslation('stream');
  return (
    <Stack spacing="01">
      <NewResultsText />

      <Text variant="xs" fw={400} color="primary.7" truncate>
        {t('editStreamForm.searching')}
      </Text>
    </Stack>
  );
}

function HeaderResultsCount({ resultsCount }: { resultsCount: number }) {
  const { t } = useTranslation('stream');

  const count = resultsCount > MAX_TENDER_PREVIEW_COUNT ? '+ 99' : resultsCount;

  return (
    <Text variant="sm" fw={600} color="primary.7" truncate>
      {count} {t('editStreamForm.results')}
    </Text>
  );
}

function HeaderNoNewResults() {
  const { t } = useTranslation('stream');
  return (
    <Stack spacing="01">
      <NewResultsText />

      <Text variant="xs" fw={400} color="gray.7" truncate>
        {t('editStreamForm.noNewResults')}
      </Text>
    </Stack>
  );
}

function HeaderNewResults({ resultsCount }: { resultsCount: number }) {
  const { t } = useTranslation('stream');

  return (
    <Group noWrap spacing="01">
      <NewResultsText />

      <Text variant="sm" fw={600} color="primary.7" truncate>
        + {resultsCount} {t('editStreamForm.tenderAbbreviation')}
      </Text>
    </Group>
  );
}

function NewResultsText() {
  const { t } = useTranslation('stream');
  return (
    <Text variant="sm" fw={500} color="gray.9" truncate>
      {t('editStreamForm.newResults')}
    </Text>
  );
}
